import { BusinessSubscriptionModel } from '@pukka/api';
import { Button, Modal, Text, Icon } from '@pukka/ui-components';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';

import RenewalPromoBanner from './renewal-promo-banner.jpeg';
import { formatCurrency, generateGroupedSchemes } from './utils';

interface BillingDiscountModalProps {
  readonly show: boolean;
  readonly discountSchemes: BusinessSubscriptionModel.DiscountScheme[];
  readonly isMobile: boolean;
  readonly fromBanner?: boolean;
  readonly onClose?: () => void;
  readonly onToggleModal?: (open: boolean) => void;
}

const tdClassName = 'border border-neutral-100 px-3 py-3 text-center';

const TITLE = 'Outlet Renewal Promo';

function BillingDiscountModal(props: BillingDiscountModalProps) {
  const { show, discountSchemes, isMobile, fromBanner, onClose, onToggleModal } = props;

  const groupedDiscountSchemes = generateGroupedSchemes(discountSchemes);

  const selectedIndex = groupedDiscountSchemes.findIndex((discount) => discount.selected);

  const handleGoToBilling = () => {
    onClose?.();

    window.location.href = '/v2/settings/billing#fromPopup';
  };

  return (
    <Modal open={show} onOpenChange={onToggleModal}>
      {!isMobile && (
        <Modal.Title>
          <Text className="w-full text-center">{TITLE}</Text>
          <Modal.Close asChild>
            <Button variant="nude" size="none" className="rounded-md">
              <Icon icon="close" color="white" />
            </Button>
          </Modal.Close>
        </Modal.Title>
      )}
      <Modal.Body className="max-h-[60vh] px-1">
        {isMobile && (
          <div className="px-4">
            <div className="text-right">
              <button
                onClick={onClose}
                type="button"
                className="border-none bg-transparent p-0 text-3xl outline-none"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="mb-4 text-xl font-bold">{TITLE}</div>
          </div>
        )}
        {!isMobile && <img src={RenewalPromoBanner} alt="Renewal Promo" className="mb-4 px-4" />}
        <div className="mb-4 px-4 text-sm">
          Maximize your savings and productivity by renewing your POS subscription now, with a <strong>special discount</strong> exclusively
          designed for our valued customers, empowering your business for success.
        </div>
        <div className="px-4">
          <table className="w-full table-fixed text-xs">
            <tbody>
              {groupedDiscountSchemes.map((scheme, idx) => {
                const startDate = scheme.start_date ? format(new Date(scheme.start_date), 'dd MMM yyyy') : '';
                const endDate = scheme.end_date ? format(new Date(scheme.end_date), 'dd MMM yyyy') : '';
                const discountedPrice = scheme.discounted_price ? `Rp${formatCurrency(scheme.discounted_price)}` : '';
                const normalPrice = scheme.normal_price ? `Rp${formatCurrency(scheme.normal_price)}` : '';
                const isDisabled = idx < selectedIndex;

                return (
                  <tr
                    key={idx}
                    className={twMerge(
                      'text-neutral-700',
                      scheme.selected && 'bg-neutral-100 text-black',
                      isDisabled && 'bg-neutral-200 text-neutral-400',
                    )}
                  >
                    <td className={tdClassName}>
                      {startDate} - {endDate}
                    </td>
                    {!isMobile && <td className={tdClassName}>{scheme.discount}%</td>}
                    <td className={tdClassName}>
                      {discountedPrice}{' '}
                      <span className={twMerge('text-red-700 line-through', isDisabled && 'text-neutral-400')}>{normalPrice}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {fromBanner && isMobile && (
          <div className="mx-4 mt-10 h-[56px] border-0 border-t border-solid border-neutral-300 pt-3 text-right">
            <Button onClick={handleGoToBilling}>Go to Billing</Button>
          </div>
        )}
      </Modal.Body>
      {fromBanner && !isMobile && <Modal.Footer onCancel={false} buttons={<Button onClick={handleGoToBilling}>Go to Billing</Button>} />}
    </Modal>
  );
}

export default BillingDiscountModal;
